import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Header from "@/components/Header";
import { Plus, Trash2, Check, X, CalendarClock, Loader2, Clock, Edit, Users, CalendarCheck, CalendarX, ClipboardList, BookOpen, BarChart3, Sparkles, User, Newspaper } from "lucide-react";
import { format, parseISO, addDays } from "date-fns";
import { de, enUS } from "date-fns/locale";
import ExperimenterTab from "@/components/ExperimenterTab";
import SuggestionTab from "@/components/SuggestionTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const { t, language } = useLanguage();
  const dateLocale = language === "de" ? de : enUS;
  
  const [activeTab, setActiveTab] = useState("appointments");
  const [appointments, setAppointments] = useState([]);
  const [studies, setStudies] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [news, setNews] = useState([]);
  const [pageStudies, setPageStudies] = useState([]);
  const [experimenters, setExperimenters] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form states
  const [newStudyName, setNewStudyName] = useState("");
  const [newStudyDescription, setNewStudyDescription] = useState("");
  const [slotDate, setSlotDate] = useState(null);
  const [slotTime, setSlotTime] = useState("10:00");
  const [session2Date, setSession2Date] = useState(null);
  const [session2Time, setSession2Time] = useState("10:00");
  const [suggestedDate, setSuggestedDate] = useState(null);
  const [suggestedTime, setSuggestedTime] = useState("10:00");
  
  // News form
  const [newsTitleDe, setNewsTitleDe] = useState("");
  const [newsTitleEn, setNewsTitleEn] = useState("");
  const [newsDescDe, setNewsDescDe] = useState("");
  const [newsDescEn, setNewsDescEn] = useState("");
  const [newsLink, setNewsLink] = useState("");

  // Page Study form
  const [psName, setPsName] = useState("");
  const [psDescDe, setPsDescDe] = useState("");
  const [psDescEn, setPsDescEn] = useState("");
  const [psAgeRange, setPsAgeRange] = useState("");
  const [psCompensation, setPsCompensation] = useState("");
  const [psEmail, setPsEmail] = useState("");
  const [psIsActive, setPsIsActive] = useState(true);
  
  const [addingStudy, setAddingStudy] = useState(false);
  const [addingSlot, setAddingSlot] = useState(false);
  const [addingNews, setAddingNews] = useState(false);
  const [addingPageStudy, setAddingPageStudy] = useState(false);
  const [slotExp1, setSlotExp1] = useState("");
  const [slotExp2, setSlotExp2] = useState("");

  // Newsletter form
  const [nlTitleDe, setNlTitleDe] = useState("");
  const [nlTitleEn, setNlTitleEn] = useState("");
  const [nlContentDe, setNlContentDe] = useState("");
  const [nlContentEn, setNlContentEn] = useState("");
  const [nlMonth, setNlMonth] = useState(String(new Date().getMonth() + 1));
  const [nlYear, setNlYear] = useState(String(new Date().getFullYear()));
  const [addingNl, setAddingNl] = useState(false);
  const [nlImageUrl, setNlImageUrl] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const MONTHS = language === "de" 
    ? ["Jan","Feb","Mär","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"]
    : ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (slotDate) setSession2Date(addDays(slotDate, 7)); }, [slotDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appts, studs, slots, newsData, pageStudsData, expsData, nlData] = await Promise.all([
        axios.get(`${API}/admin/appointments`, { withCredentials: true }),
        axios.get(`${API}/admin/studies`, { withCredentials: true }),
        axios.get(`${API}/admin/time-slots`, { withCredentials: true }),
        axios.get(`${API}/admin/news`, { withCredentials: true }),
        axios.get(`${API}/admin/page-studies`, { withCredentials: true }),
        axios.get(`${API}/admin/experimenters`, { withCredentials: true }),
        axios.get(`${API}/admin/newsletters`, { withCredentials: true })
      ]);
      setAppointments(appts.data);
      setStudies(studs.data);
      setTimeSlots(slots.data);
      setNews(newsData.data);
      setPageStudies(pageStudsData.data);
      setExperimenters(expsData.data);
      setNewsletters(nlData.data);
    } catch (error) {
      toast.error("Error loading data");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API}/admin/appointments/${id}/approve`, {}, { withCredentials: true });
      toast.success(language === "de" ? "Termin genehmigt" : "Approved");
      fetchData();
    } catch (error) { toast.error("Error"); }
  };

  const handleReject = async (id) => {
    try {
      await axios.put(`${API}/admin/appointments/${id}/reject`, {}, { withCredentials: true });
      toast.success(language === "de" ? "Termin abgelehnt" : "Rejected");
      fetchData();
    } catch (error) { toast.error("Error"); }
  };

  const handleSuggestNew = async (id) => {
    if (!suggestedDate) return;
    try {
      await axios.put(`${API}/admin/appointments/${id}/suggest-new`, { new_date: format(suggestedDate, "yyyy-MM-dd"), new_time: suggestedTime }, { withCredentials: true });
      toast.success(language === "de" ? "Neuer Termin vorgeschlagen" : "New date suggested");
      setSuggestedDate(null);
      fetchData();
    } catch (error) { toast.error("Error"); }
  };

  const handleAddStudy = async () => {
    if (!newStudyName.trim()) return;
    setAddingStudy(true);
    try {
      await axios.post(`${API}/admin/studies`, { name: newStudyName.trim(), description: newStudyDescription.trim() }, { withCredentials: true });
      toast.success(language === "de" ? "Studie hinzugefügt" : "Study added");
      setNewStudyName(""); setNewStudyDescription("");
      fetchData();
    } catch (error) { toast.error("Error"); }
    finally { setAddingStudy(false); }
  };

  const handleDeleteStudy = async (id) => {
    try {
      await axios.delete(`${API}/admin/studies/${id}`, { withCredentials: true });
      toast.success(language === "de" ? "Gelöscht" : "Deleted");
      fetchData();
    } catch (error) { toast.error("Error"); }
  };

  const handleAddSlot = async () => {
    if (!slotDate || !session2Date) return;
    setAddingSlot(true);
    try {
      await axios.post(`${API}/admin/time-slots`, { date: format(slotDate, "yyyy-MM-dd"), time: slotTime, session2_date: format(session2Date, "yyyy-MM-dd"), session2_time: session2Time, experimenter1_id: slotExp1 || null, experimenter2_id: slotExp2 || null }, { withCredentials: true });
      toast.success(language === "de" ? "Zeitfenster hinzugefügt" : "Time slot added");
      setSlotDate(null); setSession2Date(null); setSlotExp1(""); setSlotExp2("");
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Error"); }
    finally { setAddingSlot(false); }
  };

  const handleDeleteSlot = async (id) => {
    try {
      await axios.delete(`${API}/admin/time-slots/${id}`, { withCredentials: true });
      toast.success(language === "de" ? "Gelöscht" : "Deleted");
      fetchData();
    } catch (error) { toast.error("Error"); }
  };

  const handleAddNews = async () => {
    if (!newsTitleDe.trim() || !newsTitleEn.trim()) return;
    setAddingNews(true);
    try {
      await axios.post(`${API}/admin/news`, { title_de: newsTitleDe, title_en: newsTitleEn, description_de: newsDescDe, description_en: newsDescEn, link: newsLink || null }, { withCredentials: true });
      toast.success(language === "de" ? "Ankündigung hinzugefügt" : "Announcement added");
      setNewsTitleDe(""); setNewsTitleEn(""); setNewsDescDe(""); setNewsDescEn(""); setNewsLink("");
      fetchData();
    } catch (error) { toast.error("Error"); }
    finally { setAddingNews(false); }
  };

  const handleDeleteNews = async (id) => {
    try {
      await axios.delete(`${API}/admin/news/${id}`, { withCredentials: true });
      toast.success(language === "de" ? "Gelöscht" : "Deleted");
      fetchData();
    } catch (error) { toast.error("Error"); }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/admin/upload-image`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setNlImageUrl(res.data.url);
      toast.success(language === "de" ? "Bild hochgeladen" : "Image uploaded");
    } catch (e) { toast.error("Upload error"); }
    finally { setUploadingImage(false); }
  };

  const handleAddNewsletter = async () => {
    if (!nlTitleDe.trim() || !nlContentDe.trim()) return;
    setAddingNl(true);
    try {
      await axios.post(`${API}/admin/newsletters`, {
        title_de: nlTitleDe, title_en: nlTitleEn || nlTitleDe,
        content_de: nlContentDe, content_en: nlContentEn || nlContentDe,
        month: parseInt(nlMonth), year: parseInt(nlYear),
        image_url: nlImageUrl || null
      }, { withCredentials: true });
      toast.success(language === "de" ? "Newsletter erstellt" : "Newsletter created");
      setNlTitleDe(""); setNlTitleEn(""); setNlContentDe(""); setNlContentEn(""); setNlImageUrl("");
      fetchData();
    } catch (error) { toast.error("Error"); }
    finally { setAddingNl(false); }
  };

  const handleDeleteAppointment = async (id) => {
    if (!window.confirm(language === "de" ? "Termin wirklich löschen?" : "Really delete appointment?")) return;
    try {
      await axios.delete(`${API}/admin/appointments/${id}`, { withCredentials: true });
      toast.success(language === "de" ? "Gelöscht" : "Deleted");
      fetchData();
    } catch (error) { toast.error("Error"); }
  };

  const handleDeleteNewsletter = async (id) => {
    try {
      await axios.delete(`${API}/admin/newsletters/${id}`, { withCredentials: true });
      toast.success(language === "de" ? "Gelöscht" : "Deleted");
      fetchData();
    } catch (error) { toast.error("Error"); }
  };

  const handleAddPageStudy = async () => {
    if (!psName.trim() || !psDescDe.trim() || !psDescEn.trim()) return;
    setAddingPageStudy(true);
    try {
      await axios.post(`${API}/admin/page-studies`, {
        name: psName.trim(),
        description_de: psDescDe.trim(),
        description_en: psDescEn.trim(),
        age_range: psAgeRange || null,
        compensation: psCompensation || null,
        contact_email: psEmail || null,
        is_active: psIsActive
      }, { withCredentials: true });
      toast.success(language === "de" ? "Studie hinzugefügt" : "Study added");
      setPsName(""); setPsDescDe(""); setPsDescEn(""); setPsAgeRange(""); setPsCompensation(""); setPsEmail(""); setPsIsActive(true);
      fetchData();
    } catch (error) { toast.error("Error"); }
    finally { setAddingPageStudy(false); }
  };

  const handleDeletePageStudy = async (id) => {
    try {
      await axios.delete(`${API}/admin/page-studies/${id}`, { withCredentials: true });
      toast.success(language === "de" ? "Gelöscht" : "Deleted");
      fetchData();
    } catch (error) { toast.error("Error"); }
  };

  const handleTogglePageStudyActive = async (study) => {
    try {
      await axios.put(`${API}/admin/page-studies/${study.id}`, {
        ...study,
        is_active: !study.is_active
      }, { withCredentials: true });
      fetchData();
    } catch (error) { toast.error("Error"); }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { className: "bg-amber-100 text-amber-800", label: t("admin.pending") },
      approved: { className: "bg-green-100 text-green-800", label: t("admin.approved") },
      rejected: { className: "bg-red-100 text-red-800", label: t("admin.rejected") },
      suggested: { className: "bg-blue-100 text-blue-800", label: t("admin.suggested") }
    };
    const v = variants[status] || variants.pending;
    return <Badge className={v.className}>{v.label}</Badge>;
  };

  if (loading) return <div className="min-h-screen bg-sand-50 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-forest-800" /></div>;

  return (
    <div className="min-h-screen bg-sand-50">
      <Header />
      <main className="px-4 md:px-12 py-6 max-w-7xl mx-auto">
        <h1 className="font-outfit text-2xl font-medium text-forest-900 mb-6">{t("admin.dashboard")}</h1>

        {/* Statistics Overview */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="admin-stats-overview">
          <Card className="bg-white rounded-xl border-stone-200 shadow-sm">
            <CardContent className="p-4 flex flex-col items-center text-center">
              <div className="h-9 w-9 rounded-lg bg-forest-100 flex items-center justify-center mb-2">
                <ClipboardList className="h-4 w-4 text-forest-700" />
              </div>
              <p className="text-2xl font-semibold text-forest-900" data-testid="stat-total-appointments">{appointments.length}</p>
              <p className="text-xs text-stone-500 mt-0.5">{language === "de" ? "Gesamt" : "Total"}</p>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border-stone-200 shadow-sm">
            <CardContent className="p-4 flex flex-col items-center text-center">
              <div className="h-9 w-9 rounded-lg bg-amber-100 flex items-center justify-center mb-2">
                <Clock className="h-4 w-4 text-amber-700" />
              </div>
              <p className="text-2xl font-semibold text-amber-700" data-testid="stat-pending-appointments">{appointments.filter(a => a.status === "pending").length}</p>
              <p className="text-xs text-stone-500 mt-0.5">{language === "de" ? "Ausstehend" : "Pending"}</p>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border-stone-200 shadow-sm">
            <CardContent className="p-4 flex flex-col items-center text-center">
              <div className="h-9 w-9 rounded-lg bg-green-100 flex items-center justify-center mb-2">
                <CalendarCheck className="h-4 w-4 text-green-700" />
              </div>
              <p className="text-2xl font-semibold text-green-700" data-testid="stat-approved-appointments">{appointments.filter(a => a.status === "approved").length}</p>
              <p className="text-xs text-stone-500 mt-0.5">{language === "de" ? "Genehmigt" : "Approved"}</p>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border-stone-200 shadow-sm">
            <CardContent className="p-4 flex flex-col items-center text-center">
              <div className="h-9 w-9 rounded-lg bg-red-100 flex items-center justify-center mb-2">
                <CalendarX className="h-4 w-4 text-red-700" />
              </div>
              <p className="text-2xl font-semibold text-red-700" data-testid="stat-rejected-appointments">{appointments.filter(a => a.status === "rejected").length}</p>
              <p className="text-xs text-stone-500 mt-0.5">{language === "de" ? "Abgelehnt" : "Rejected"}</p>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border-stone-200 shadow-sm">
            <CardContent className="p-4 flex flex-col items-center text-center">
              <div className="h-9 w-9 rounded-lg bg-blue-100 flex items-center justify-center mb-2">
                <BarChart3 className="h-4 w-4 text-blue-700" />
              </div>
              <p className="text-2xl font-semibold text-blue-700" data-testid="stat-available-slots">{timeSlots.filter(s => !s.is_booked).length}</p>
              <p className="text-xs text-stone-500 mt-0.5">{language === "de" ? "Freie Termine" : "Open Slots"}</p>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border-stone-200 shadow-sm">
            <CardContent className="p-4 flex flex-col items-center text-center">
              <div className="h-9 w-9 rounded-lg bg-purple-100 flex items-center justify-center mb-2">
                <BookOpen className="h-4 w-4 text-purple-700" />
              </div>
              <p className="text-2xl font-semibold text-purple-700" data-testid="stat-active-studies">{pageStudies.filter(s => s.is_active).length}</p>
              <p className="text-xs text-stone-500 mt-0.5">{language === "de" ? "Aktive Studien" : "Active Studies"}</p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-white border border-stone-200 rounded-xl p-1 flex-wrap">
            <TabsTrigger value="appointments" className="rounded-lg text-sm data-[state=active]:bg-forest-800 data-[state=active]:text-white">{t("admin.appointments")} ({appointments.length})</TabsTrigger>
            <TabsTrigger value="suggestions" className="rounded-lg text-sm data-[state=active]:bg-forest-800 data-[state=active]:text-white"><Sparkles className="h-3 w-3 mr-1" />{language === "de" ? "Vorschläge" : "Suggestions"}</TabsTrigger>
            <TabsTrigger value="slots" className="rounded-lg text-sm data-[state=active]:bg-forest-800 data-[state=active]:text-white">{t("admin.timeSlots")} ({timeSlots.length})</TabsTrigger>
            <TabsTrigger value="experimenters" className="rounded-lg text-sm data-[state=active]:bg-forest-800 data-[state=active]:text-white"><User className="h-3 w-3 mr-1" />{language === "de" ? "Experimenter" : "Experimenters"} ({experimenters.length})</TabsTrigger>
            <TabsTrigger value="studies" className="rounded-lg text-sm data-[state=active]:bg-forest-800 data-[state=active]:text-white">{t("admin.studiesTab")} ({studies.length})</TabsTrigger>
            <TabsTrigger value="page-studies" className="rounded-lg text-sm data-[state=active]:bg-forest-800 data-[state=active]:text-white">{language === "de" ? "Studien-Seite" : "Studies Page"} ({pageStudies.length})</TabsTrigger>
            <TabsTrigger value="news" className="rounded-lg text-sm data-[state=active]:bg-forest-800 data-[state=active]:text-white">{t("admin.newsTab")} ({news.length})</TabsTrigger>
            <TabsTrigger value="newsletters" className="rounded-lg text-sm data-[state=active]:bg-forest-800 data-[state=active]:text-white"><Newspaper className="h-3 w-3 mr-1" />Newsletter ({newsletters.length})</TabsTrigger>
          </TabsList>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
              <CardHeader><CardTitle className="font-outfit text-lg text-forest-800">{t("admin.appointmentRequests")}</CardTitle></CardHeader>
              <CardContent>
                {appointments.length === 0 ? <p className="text-stone-500 text-center py-6">{t("admin.noAppointments")}</p> : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("admin.surnameCol")}</TableHead>
                          <TableHead>{t("admin.emailCol")}</TableHead>
                          <TableHead>{t("admin.childCol")}</TableHead>
                          <TableHead>{t("admin.studyCol")}</TableHead>
                          <TableHead>{t("booking.session1")}</TableHead>
                          <TableHead>{t("booking.session2")}</TableHead>
                          <TableHead>{t("admin.statusCol")}</TableHead>
                          <TableHead>{t("admin.actionsCol")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {appointments.map((apt) => (
                          <TableRow key={apt.id}>
                            <TableCell className="font-medium">{apt.surname}</TableCell>
                            <TableCell className="text-xs">{apt.parent_email || "-"}</TableCell>
                            <TableCell className="text-xs">{apt.child_age ? `${apt.child_age}J` : "-"}{apt.child_gender ? ` / ${apt.child_gender}` : ""}</TableCell>
                            <TableCell>{apt.study_name}</TableCell>
                            <TableCell className="text-xs">{format(parseISO(apt.session1_date), "dd.MM.yy")} {apt.session1_time}</TableCell>
                            <TableCell className="text-xs">{format(parseISO(apt.session2_date), "dd.MM.yy")} {apt.session2_time}</TableCell>
                            <TableCell>{getStatusBadge(apt.status)}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {apt.status !== "approved" && (
                                  <Button size="sm" variant="outline" className="text-green-600 h-7 w-7 p-0" onClick={() => handleApprove(apt.id)} data-testid={`approve-btn-${apt.id}`} title={language === "de" ? "Genehmigen" : "Approve"}><Check className="h-3 w-3" /></Button>
                                )}
                                {apt.status !== "rejected" && (
                                  <Button size="sm" variant="outline" className="text-red-600 h-7 w-7 p-0" onClick={() => handleReject(apt.id)} data-testid={`reject-btn-${apt.id}`} title={language === "de" ? "Ablehnen" : "Reject"}><X className="h-3 w-3" /></Button>
                                )}
                                <Popover>
                                  <PopoverTrigger asChild><Button size="sm" variant="outline" className="text-blue-600 h-7 w-7 p-0" data-testid={`suggest-btn-${apt.id}`} title={language === "de" ? "Neuen Termin vorschlagen" : "Suggest new date"}><CalendarClock className="h-3 w-3" /></Button></PopoverTrigger>
                                  <PopoverContent className="w-auto p-3">
                                    <p className="text-sm font-medium mb-2">{t("admin.suggestNewDate")}</p>
                                    <Calendar mode="single" selected={suggestedDate} onSelect={setSuggestedDate} locale={dateLocale} />
                                    <div className="mt-2"><Label className="text-xs">{t("admin.time")}</Label><Input type="time" value={suggestedTime} onChange={(e) => setSuggestedTime(e.target.value)} className="mt-1 h-8" /></div>
                                    <Button className="w-full mt-3 bg-forest-800 rounded-full h-8 text-sm" onClick={() => handleSuggestNew(apt.id)} disabled={!suggestedDate}>{t("admin.suggest")}</Button>
                                  </PopoverContent>
                                </Popover>
                                <Button size="sm" variant="outline" className="text-stone-400 hover:text-red-600 h-7 w-7 p-0" onClick={() => handleDeleteAppointment(apt.id)} data-testid={`delete-apt-${apt.id}`} title={language === "de" ? "Löschen" : "Delete"}><Trash2 className="h-3 w-3" /></Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Suggestions Tab */}
          <TabsContent value="suggestions">
            <SuggestionTab language={language} onSlotCreated={fetchData} />
          </TabsContent>

          {/* Time Slots Tab */}
          <TabsContent value="slots">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
                <CardHeader><CardTitle className="font-outfit text-lg text-forest-800">{t("admin.addTimeSlot")}</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div><Label className="text-sm">{t("admin.session1Date")}</Label><Calendar mode="single" selected={slotDate} onSelect={setSlotDate} locale={dateLocale} className="rounded-xl border mt-1" /></div>
                  <div><Label className="text-sm">{t("admin.session1Time")}</Label><Input type="time" value={slotTime} onChange={(e) => setSlotTime(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" /></div>
                  <div className="border-t pt-3"><Label className="text-sm">{t("admin.session2DateAuto")}</Label><Calendar mode="single" selected={session2Date} onSelect={setSession2Date} locale={dateLocale} className="rounded-xl border mt-1" /></div>
                  <div><Label className="text-sm">{t("admin.session2Time")}</Label><Input type="time" value={session2Time} onChange={(e) => setSession2Time(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" /></div>
                  {experimenters.length > 0 && (
                    <div className="border-t pt-3 space-y-3">
                      <div>
                        <Label className="text-sm">{language === "de" ? "Experimenter Sitzung 1" : "Experimenter Session 1"}</Label>
                        <Select value={slotExp1} onValueChange={setSlotExp1}>
                          <SelectTrigger className="mt-1 bg-stone-50 rounded-xl"><SelectValue placeholder={language === "de" ? "Optional" : "Optional"} /></SelectTrigger>
                          <SelectContent>
                            {experimenters.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm">{language === "de" ? "Experimenter Sitzung 2" : "Experimenter Session 2"}</Label>
                        <Select value={slotExp2} onValueChange={setSlotExp2}>
                          <SelectTrigger className="mt-1 bg-stone-50 rounded-xl"><SelectValue placeholder={language === "de" ? "Optional" : "Optional"} /></SelectTrigger>
                          <SelectContent>
                            {experimenters.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  <Button className="w-full bg-forest-800 hover:bg-forest-900 rounded-full" onClick={handleAddSlot} disabled={!slotDate || !session2Date || addingSlot}>
                    {addingSlot ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}{t("admin.addSlotBtn")}
                  </Button>
                </CardContent>
              </Card>
              <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
                <CardHeader><CardTitle className="font-outfit text-lg text-forest-800">{t("admin.availableSlots")}</CardTitle></CardHeader>
                <CardContent>
                  {timeSlots.length === 0 ? <p className="text-stone-500 text-center py-6">{t("admin.noSlots")}</p> : (
                    <div className="max-h-96 overflow-y-auto space-y-2">
                      {timeSlots.sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time)).map((slot) => (
                        <div key={slot.id} className={`p-3 rounded-xl border ${slot.is_booked ? "bg-amber-50 border-amber-200" : "bg-stone-50 border-stone-200"}`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Clock className="h-3 w-3 text-forest-800" />
                                <span className="font-medium text-forest-900 text-sm">{format(parseISO(slot.date), "dd.MM.yyyy")} - {slot.time}</span>
                                {slot.is_booked && <Badge className="bg-amber-100 text-amber-800 text-xs">{t("admin.booked")}</Badge>}
                              </div>
                              <p className="text-xs text-stone-500">{t("booking.session2")}: {format(parseISO(slot.session2_date), "dd.MM.yyyy")} - {slot.session2_time}</p>
                              {(slot.experimenter1_id || slot.experimenter2_id) && (
                                <p className="text-xs text-forest-600 mt-0.5">
                                  {slot.experimenter1_id && <><User className="h-3 w-3 inline mr-0.5" />{experimenters.find(e => e.id === slot.experimenter1_id)?.name || "?"}</>}
                                  {slot.experimenter2_id && <> → {experimenters.find(e => e.id === slot.experimenter2_id)?.name || "?"}</>}
                                </p>
                              )}
                            </div>
                            <Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={() => handleDeleteSlot(slot.id)}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Experimenters Tab */}
          <TabsContent value="experimenters">
            <ExperimenterTab language={language} onDataChange={fetchData} />
          </TabsContent>

          {/* Booking Studies Tab */}
          <TabsContent value="studies">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
                <CardHeader><CardTitle className="font-outfit text-lg text-forest-800">{t("admin.addStudy")}</CardTitle><CardDescription className="text-sm">{language === "de" ? "Studien für die Terminbuchung" : "Studies for appointment booking"}</CardDescription></CardHeader>
                <CardContent className="space-y-3">
                  <div><Label className="text-sm">{t("admin.studyName")}</Label><Input placeholder={t("admin.studyNamePlaceholder")} value={newStudyName} onChange={(e) => setNewStudyName(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" /></div>
                  <div><Label className="text-sm">{t("admin.studyDescription")}</Label><Input placeholder={t("admin.studyDescPlaceholder")} value={newStudyDescription} onChange={(e) => setNewStudyDescription(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" /></div>
                  <Button className="w-full bg-forest-800 hover:bg-forest-900 rounded-full" onClick={handleAddStudy} disabled={!newStudyName.trim() || addingStudy}>
                    {addingStudy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}{t("admin.addStudyBtn")}
                  </Button>
                </CardContent>
              </Card>
              <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
                <CardHeader><CardTitle className="font-outfit text-lg text-forest-800">{t("admin.activeStudies")}</CardTitle></CardHeader>
                <CardContent>
                  {studies.length === 0 ? <p className="text-stone-500 text-center py-6">{t("admin.noStudies")}</p> : (
                    <div className="space-y-2">
                      {studies.map((study) => (
                        <div key={study.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-xl">
                          <div><p className="font-medium text-forest-900 text-sm">{study.name}</p>{study.description && <p className="text-xs text-stone-500">{study.description}</p>}</div>
                          <Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={() => handleDeleteStudy(study.id)}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Page Studies Tab */}
          <TabsContent value="page-studies">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-outfit text-lg text-forest-800">{language === "de" ? "Studie zur Seite hinzufügen" : "Add Study to Page"}</CardTitle>
                  <CardDescription className="text-sm">{language === "de" ? "Studien für die öffentliche Studien-Seite" : "Studies for the public Studies page"}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div><Label className="text-sm">{language === "de" ? "Name" : "Name"}</Label><Input placeholder="z.B. Spark" value={psName} onChange={(e) => setPsName(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" /></div>
                  <div><Label className="text-sm">{language === "de" ? "Beschreibung (Deutsch)" : "Description (German)"}</Label><Textarea placeholder="Beschreibung auf Deutsch" value={psDescDe} onChange={(e) => setPsDescDe(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" rows={2} /></div>
                  <div><Label className="text-sm">{language === "de" ? "Beschreibung (Englisch)" : "Description (English)"}</Label><Textarea placeholder="Description in English" value={psDescEn} onChange={(e) => setPsDescEn(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" rows={2} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-sm">{language === "de" ? "Altersbereich" : "Age Range"}</Label><Input placeholder="z.B. 10-14 Jahre" value={psAgeRange} onChange={(e) => setPsAgeRange(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" /></div>
                    <div><Label className="text-sm">{language === "de" ? "Vergütung" : "Compensation"}</Label><Input placeholder="z.B. 12€/h" value={psCompensation} onChange={(e) => setPsCompensation(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" /></div>
                  </div>
                  <div><Label className="text-sm">{language === "de" ? "Kontakt E-Mail" : "Contact Email"}</Label><Input placeholder="studie@uni-trier.de" value={psEmail} onChange={(e) => setPsEmail(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" /></div>
                  <div className="flex items-center gap-3">
                    <Switch checked={psIsActive} onCheckedChange={setPsIsActive} />
                    <Label className="text-sm">{language === "de" ? "Aktiv (wird auf der Seite angezeigt)" : "Active (shown on page)"}</Label>
                  </div>
                  <Button className="w-full bg-forest-800 hover:bg-forest-900 rounded-full" onClick={handleAddPageStudy} disabled={!psName.trim() || !psDescDe.trim() || !psDescEn.trim() || addingPageStudy}>
                    {addingPageStudy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}{language === "de" ? "Studie hinzufügen" : "Add Study"}
                  </Button>
                </CardContent>
              </Card>
              <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
                <CardHeader><CardTitle className="font-outfit text-lg text-forest-800">{language === "de" ? "Studien auf der Seite" : "Studies on Page"}</CardTitle></CardHeader>
                <CardContent>
                  {pageStudies.length === 0 ? <p className="text-stone-500 text-center py-6">{language === "de" ? "Keine Studien" : "No studies"}</p> : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {pageStudies.map((study) => (
                        <div key={study.id} className={`p-3 rounded-xl border ${study.is_active ? "bg-green-50 border-green-200" : "bg-stone-50 border-stone-200"}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-medium text-forest-900 text-sm">{study.name}</p>
                                <Badge className={study.is_active ? "bg-green-100 text-green-800 text-xs" : "bg-stone-200 text-stone-600 text-xs"}>
                                  {study.is_active ? (language === "de" ? "Aktiv" : "Active") : (language === "de" ? "Inaktiv" : "Inactive")}
                                </Badge>
                              </div>
                              <p className="text-xs text-stone-500 line-clamp-2">{language === "de" ? study.description_de : study.description_en}</p>
                              {study.age_range && <p className="text-xs text-stone-400 mt-1">{study.age_range}</p>}
                            </div>
                            <div className="flex gap-1 ml-2">
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleTogglePageStudyActive(study)}>
                                {study.is_active ? <X className="h-3 w-3 text-amber-600" /> : <Check className="h-3 w-3 text-green-600" />}
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={() => handleDeletePageStudy(study.id)}><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* News Tab */}
          <TabsContent value="news">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
                <CardHeader><CardTitle className="font-outfit text-lg text-forest-800">{t("admin.addNews")}</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div><Label className="text-sm">{t("admin.newsTitleDe")}</Label><Input placeholder="Titel auf Deutsch" value={newsTitleDe} onChange={(e) => setNewsTitleDe(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" /></div>
                  <div><Label className="text-sm">{t("admin.newsTitleEn")}</Label><Input placeholder="Title in English" value={newsTitleEn} onChange={(e) => setNewsTitleEn(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" /></div>
                  <div><Label className="text-sm">{t("admin.newsDescDe")}</Label><Textarea placeholder="Beschreibung auf Deutsch" value={newsDescDe} onChange={(e) => setNewsDescDe(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" rows={3} /></div>
                  <div><Label className="text-sm">{t("admin.newsDescEn")}</Label><Textarea placeholder="Description in English" value={newsDescEn} onChange={(e) => setNewsDescEn(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" rows={3} /></div>
                  <div><Label className="text-sm">{t("admin.newsLink")}</Label><Input placeholder={t("admin.newsLinkPlaceholder")} value={newsLink} onChange={(e) => setNewsLink(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" /></div>
                  <Button className="w-full bg-forest-800 hover:bg-forest-900 rounded-full" onClick={handleAddNews} disabled={!newsTitleDe.trim() || !newsTitleEn.trim() || addingNews}>
                    {addingNews ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}{t("admin.addNewsBtn")}
                  </Button>
                </CardContent>
              </Card>
              <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
                <CardHeader><CardTitle className="font-outfit text-lg text-forest-800">{t("admin.allNews")}</CardTitle></CardHeader>
                <CardContent>
                  {news.length === 0 ? <p className="text-stone-500 text-center py-6">{t("admin.noNews")}</p> : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {news.map((item) => (
                        <div key={item.id} className="p-3 bg-stone-50 rounded-xl">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-forest-900 text-sm">{language === "de" ? item.title_de : item.title_en}</p>
                              <p className="text-xs text-stone-500 mt-1 line-clamp-2">{language === "de" ? item.description_de : item.description_en}</p>
                            </div>
                            <Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0 flex-shrink-0" onClick={() => handleDeleteNews(item.id)}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Newsletter Tab */}
          <TabsContent value="newsletters">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
                <CardHeader><CardTitle className="font-outfit text-lg text-forest-800">{language === "de" ? "Newsletter erstellen" : "Create Newsletter"}</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">{language === "de" ? "Monat" : "Month"}</Label>
                      <Select value={nlMonth} onValueChange={setNlMonth}>
                        <SelectTrigger className="mt-1 bg-stone-50 rounded-xl"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {MONTHS.map((m, i) => <SelectItem key={`month-${i+1}`} value={String(i + 1)}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">{language === "de" ? "Jahr" : "Year"}</Label>
                      <Input type="number" value={nlYear} onChange={(e) => setNlYear(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" />
                    </div>
                  </div>
                  <div><Label className="text-sm">{language === "de" ? "Titel (Deutsch)" : "Title (German)"}</Label><Input placeholder="Newsletter Titel" value={nlTitleDe} onChange={(e) => setNlTitleDe(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" /></div>
                  <div><Label className="text-sm">{language === "de" ? "Titel (Englisch)" : "Title (English)"}</Label><Input placeholder="Newsletter Title" value={nlTitleEn} onChange={(e) => setNlTitleEn(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" /></div>
                  <div><Label className="text-sm">{language === "de" ? "Inhalt (Deutsch)" : "Content (German)"}</Label><Textarea placeholder="Newsletter Inhalt..." value={nlContentDe} onChange={(e) => setNlContentDe(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" rows={6} /></div>
                  <div><Label className="text-sm">{language === "de" ? "Inhalt (Englisch)" : "Content (English)"}</Label><Textarea placeholder="Newsletter content..." value={nlContentEn} onChange={(e) => setNlContentEn(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" rows={6} /></div>
                  <div>
                    <Label className="text-sm">{language === "de" ? "Titelbild (optional)" : "Cover Image (optional)"}</Label>
                    <div className="mt-1 flex items-center gap-3">
                      <Input type="file" accept="image/*" onChange={handleUploadImage} className="bg-stone-50 rounded-xl text-sm" disabled={uploadingImage} />
                      {uploadingImage && <Loader2 className="h-4 w-4 animate-spin text-forest-800" />}
                    </div>
                    {nlImageUrl && (
                      <div className="mt-2 relative">
                        <img src={nlImageUrl} alt="Preview" className="h-20 rounded-lg object-cover" />
                        <button onClick={() => setNlImageUrl("")} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs"><X className="h-3 w-3" /></button>
                      </div>
                    )}
                  </div>
                  <Button className="w-full bg-forest-800 hover:bg-forest-900 rounded-full" onClick={handleAddNewsletter} disabled={!nlTitleDe.trim() || !nlContentDe.trim() || addingNl}>
                    {addingNl ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                    {language === "de" ? "Newsletter erstellen" : "Create Newsletter"}
                  </Button>
                </CardContent>
              </Card>
              <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
                <CardHeader><CardTitle className="font-outfit text-lg text-forest-800">{language === "de" ? "Alle Newsletters" : "All Newsletters"}</CardTitle></CardHeader>
                <CardContent>
                  {newsletters.length === 0 ? <p className="text-stone-500 text-center py-6">{language === "de" ? "Keine Newsletters" : "No newsletters"}</p> : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {newsletters.map((nl) => (
                        <div key={nl.id} className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl">
                          <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: nl.cover_color || "#2C5E4E" }}>
                            {MONTHS[nl.month - 1]}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-forest-900 text-sm truncate">{language === "de" ? nl.title_de : nl.title_en}</p>
                            <p className="text-xs text-stone-500">{MONTHS[nl.month - 1]} {nl.year}</p>
                          </div>
                          <Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0 flex-shrink-0" onClick={() => handleDeleteNewsletter(nl.id)}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
